import { CreditCard, Landmark, TrendingUp, Wallet } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Account, AccountType } from '@/types'

interface AccountCardProps {
  account: Account
  institutionName?: string
}

const accountTypeConfig: Record<
  AccountType,
  { icon: typeof CreditCard; label: string; chipClass: string }
> = {
  depository: {
    icon: Landmark,
    label: 'Checking/Savings',
    chipClass: 'bg-primary-600/10 text-primary-600',
  },
  credit: {
    icon: CreditCard,
    label: 'Credit card',
    chipClass: 'bg-warning-500/10 text-warning-600',
  },
  investment: {
    icon: TrendingUp,
    label: 'Investment',
    chipClass: 'bg-success-500/10 text-success-600',
  },
  loan: {
    icon: Wallet,
    label: 'Loan',
    chipClass: 'bg-info-500/10 text-info-500',
  },
}

function formatCurrency(amount: number, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount)
}

/**
 * A single account row inside the Accounts hearth-card: type icon chip,
 * name + type/mask, balance right-aligned in rounded numerals.
 */
export function AccountCard({ account, institutionName }: AccountCardProps) {
  const config = accountTypeConfig[account.type]
  const Icon = config.icon

  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <span
        className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
          config.chipClass
        )}
      >
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-gray-900">{account.name}</p>
        <p className="truncate text-xs text-gray-500">
          {config.label} · ••••{account.mask}
          {institutionName && ` · ${institutionName}`}
        </p>
      </div>
      {account.balance !== undefined ? (
        <p className="shrink-0 font-rounded text-sm font-semibold tabular-nums text-gray-900">
          {formatCurrency(account.balance, account.currency)}
        </p>
      ) : (
        <p className="shrink-0 text-xs text-gray-400">Balance unavailable</p>
      )}
    </div>
  )
}
